import { z } from 'zod';
import { targetingSchema } from './targeting.js';
// --- Status registry (Single Source of Truth) ---
export const EXPERIMENT_STATUSES = ['draft', 'running', 'paused', 'completed'];
// --- Mode registry ---
export const EXPERIMENT_MODES = ['ab', 'bandit'];
const BANDIT_METRIC_ERROR = 'metric is required when mode is "bandit"';
/** Valid status transitions. Key = current status, value = allowed next statuses. */
export const EXPERIMENT_STATUS_TRANSITIONS = {
    draft: ['running'],
    running: ['paused', 'completed'],
    paused: ['running'],
    completed: [],
};
// --- Variant ---
export const experimentVariantSchema = z
    .object({
    id: z
        .string()
        .min(1)
        .max(100)
        .regex(/^[a-zA-Z0-9_-]+$/)
        .meta({
        description: 'Variant identifier (alphanumeric, underscore, or hyphen)',
        example: 'control',
    }),
    name: z
        .string()
        .min(1)
        .max(200)
        .meta({ description: 'Human-readable variant name', example: 'Original Copy' }),
    weight: z.number().int().min(0).max(10000).meta({
        description: 'Traffic allocation in basis points (0-10000, all variants must sum to 10000)',
    }),
})
    .meta({ id: 'ExperimentVariant', description: 'A variant in an A/B or bandit experiment' });
// --- Create experiment request ---
export const createExperimentRequestSchema = z
    .object({
    name: z
        .string()
        .min(1)
        .max(200)
        .meta({ description: 'Human-readable experiment name', example: 'Hero Copy Test' }),
    description: z
        .string()
        .max(2000)
        .meta({ description: 'Optional description of the experiment hypothesis' })
        .optional(),
    variants: z
        .array(experimentVariantSchema)
        .meta({ description: 'Experiment variants (2-20, weights must sum to 10000)' })
        .min(2)
        .max(20)
        .refine((vs) => vs.reduce((s, v) => s + v.weight, 0) === 10000, {
        error: 'Variant weights must sum to 10000',
    }),
    targeting: targetingSchema.optional(),
    exclusion_group_id: z
        .string()
        .meta({
        description: 'Mutual exclusion group ID (ensures no visitor is in multiple experiments)',
    })
        .optional(),
    mode: z
        .enum(EXPERIMENT_MODES)
        .meta({
        description: 'Experiment mode: ab (fixed weights) or bandit (Thompson Sampling auto-optimization)',
    })
        .default('ab'),
    metric: z
        .string()
        .max(200)
        .meta({
        description: 'Conversion event name for bandit mode (required when mode is bandit)',
        example: 'purchase_completed',
    })
        .optional(),
})
    .meta({ id: 'CreateExperimentRequest', description: 'Create a new experiment' })
    .superRefine((data, ctx) => {
    if (data.mode === 'bandit' && !data.metric) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['metric'],
            message: BANDIT_METRIC_ERROR,
        });
    }
});
// --- Update experiment request ---
export const updateExperimentRequestSchema = z
    .object({
    name: z.string().min(1).max(200).optional(),
    description: z.string().max(2000).optional(),
    variants: z
        .array(experimentVariantSchema)
        .min(2)
        .max(20)
        .refine((vs) => vs.reduce((s, v) => s + v.weight, 0) === 10000, {
        error: 'Variant weights must sum to 10000',
    })
        .optional(),
    targeting: targetingSchema.nullable().optional(),
    exclusion_group_id: z.string().nullable().optional(),
    mode: z.enum(EXPERIMENT_MODES).optional(),
    metric: z.string().max(200).nullable().optional(),
})
    .meta({
    id: 'UpdateExperimentRequest',
    description: 'Update an existing experiment (all fields optional)',
})
    .superRefine((data, ctx) => {
    if (data.mode === 'bandit' && !data.metric) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['metric'],
            message: BANDIT_METRIC_ERROR,
        });
    }
});
// --- Status change request ---
export const experimentStatusChangeSchema = z
    .object({
    status: z.enum(EXPERIMENT_STATUSES).meta({
        description: 'Target status (valid transitions: draft→running, running→paused/completed, paused→running)',
    }),
})
    .meta({ id: 'ExperimentStatusChange', description: 'Change experiment status' });
//# sourceMappingURL=experiments.js.map